export class AlertSynth {
  private ctx: AudioContext;
  private output: GainNode;
  private isPlaying = false;

  private osc: OscillatorNode | null = null;
  private gain: GainNode | null = null;
  private lfo: OscillatorNode | null = null;
  private lfoGain: GainNode | null = null;
  private stopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  // Track pending nodes that need cleanup when timeout is cancelled
  private pendingOscToStop: OscillatorNode | null = null;
  private pendingLfoToStop: OscillatorNode | null = null;

  constructor(ctx: AudioContext, output: GainNode) {
    this.ctx = ctx;
    this.output = output;
  }

  start(): void {
    // Cancel any pending stop timeout and clean up pending nodes
    if (this.stopTimeoutId !== null) {
      clearTimeout(this.stopTimeoutId);
      this.stopTimeoutId = null;
      // Stop the pending nodes that were waiting to be stopped
      this.stopPendingNodes();
    }

    // If already playing, don't restart
    if (this.isPlaying) return;

    // Clean up any leftover nodes from interrupted stop
    this.cleanupNodes();

    const now = this.ctx.currentTime;

    // Main oscillator - 800Hz warning tone
    this.osc = this.ctx.createOscillator();
    this.osc.type = 'square';
    this.osc.frequency.setValueAtTime(800, now);

    // Gain node
    this.gain = this.ctx.createGain();
    this.gain.gain.setValueAtTime(0, now);
    this.gain.gain.linearRampToValueAtTime(0.15, now + 0.05);

    // LFO for rapid pulsing
    this.lfo = this.ctx.createOscillator();
    this.lfo.type = 'square';
    this.lfo.frequency.setValueAtTime(8, now); // 8Hz pulsing

    this.lfoGain = this.ctx.createGain();
    this.lfoGain.gain.setValueAtTime(0.15, now);

    // Connect
    this.lfo.connect(this.lfoGain);
    this.lfoGain.connect(this.gain.gain);
    this.osc.connect(this.gain);
    this.gain.connect(this.output);

    // Start
    this.osc.start(now);
    this.lfo.start(now);

    this.isPlaying = true;
  }

  stop(): void {
    // Always clean up any pending stop timeout
    if (this.stopTimeoutId !== null) {
      clearTimeout(this.stopTimeoutId);
      this.stopTimeoutId = null;
    }

    // Always stop any pending nodes from previous stop calls
    this.stopPendingNodes();

    if (!this.isPlaying) return;

    const now = this.ctx.currentTime;

    // Quick fade out
    if (this.gain) {
      this.gain.gain.cancelScheduledValues(now);
      this.gain.gain.setValueAtTime(this.gain.gain.value, now);
      this.gain.gain.linearRampToValueAtTime(0, now + 0.05);
    }

    this.isPlaying = false;

    // Store references to pending nodes (in case timeout is cancelled)
    this.pendingOscToStop = this.osc;
    this.pendingLfoToStop = this.lfo;

    // Clear current references immediately
    this.osc = null;
    this.gain = null;
    this.lfo = null;
    this.lfoGain = null;

    // Stop oscillators after fade
    this.stopTimeoutId = setTimeout(() => {
      this.stopTimeoutId = null;
      this.stopPendingNodes();
    }, 100);
  }

  private stopPendingNodes(): void {
    try {
      this.pendingOscToStop?.stop();
      this.pendingLfoToStop?.stop();
    } catch {
      // Oscillator may already be stopped
    }
    this.pendingOscToStop = null;
    this.pendingLfoToStop = null;
  }

  private cleanupNodes(): void {
    try {
      this.osc?.stop();
      this.lfo?.stop();
    } catch {
      // Oscillator may already be stopped
    }
    this.osc = null;
    this.gain = null;
    this.lfo = null;
    this.lfoGain = null;
  }
}
